import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { resolveImage } from "@/lib/image-map";
import { Users, MousePointerClick, Coins, Sparkles, ArrowUpRight, TrendingUp, ShoppingBag, Pencil, Mail } from "lucide-react";
import { useMemo, useState } from "react";
import { Area, AreaChart, Bar, BarChart, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { getAdminAnalytics, sendCreatorNudge, seedTestProducts } from "@/lib/admin-audit.functions";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/")({
  head: () => ({
    meta: [
      { title: "Overview — Admin" },
      { name: "description", content: "Operator overview: live KPIs across creators, clicks, commissions and trending products." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminDashboard,
});

function since(days: number) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString();
}

function fmtMoney(n: number, ccy = "GBP") {
  try {
    return new Intl.NumberFormat("en-GB", { style: "currency", currency: ccy, maximumFractionDigits: 0 }).format(n);
  } catch {
    return `£${n.toFixed(0)}`;
  }
}

function fmtRelative(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function AdminDashboard() {
  const sevenDaysAgo = useMemo(() => since(7), []);
  const thirtyDaysAgo = useMemo(() => since(30), []);

  const metrics = useQuery({
    queryKey: ["admin-overview-metrics", sevenDaysAgo],
    queryFn: async () => {
      const [liveCreators, pendingCreators, totalCreators, clicks7d, clicks30d, commissions, products] = await Promise.all([
        supabase.from("creators").select("id", { count: "exact", head: true }).eq("status", "live"),
        supabase.from("creators").select("id", { count: "exact", head: true }).eq("status", "pending"),
        supabase.from("creators").select("id", { count: "exact", head: true }),
        supabase.from("clicks").select("id", { count: "exact", head: true }).gte("created_at", sevenDaysAgo),
        supabase.from("clicks").select("id", { count: "exact", head: true }).gte("created_at", thirtyDaysAgo),
        supabase.from("commissions").select("commission_amount,status,currency"),
        supabase.from("products").select("id", { count: "exact", head: true }),
      ]);

      const commissionRows = (commissions.data ?? []) as { commission_amount: number; status: string; currency: string }[];
      const pendingCommission = commissionRows
        .filter((r) => r.status === "pending")
        .reduce((sum, r) => sum + Number(r.commission_amount || 0), 0);
      const paidCommission = commissionRows
        .filter((r) => r.status === "paid" || r.status === "approved")
        .reduce((sum, r) => sum + Number(r.commission_amount || 0), 0);

      return {
        liveCreators: liveCreators.count ?? 0,
        pendingCreators: pendingCreators.count ?? 0,
        totalCreators: totalCreators.count ?? 0,
        clicks7d: clicks7d.count ?? 0,
        clicks30d: clicks30d.count ?? 0,
        pendingCommission,
        paidCommission,
        commissionsCount: commissionRows.length,
        products: products.count ?? 0,
      };
    },
  });

  const settings = useQuery({
    queryKey: ["platform-settings"],
    queryFn: async () => {
      const { data } = await supabase
        .from("platform_settings")
        .select("platform_commission_rate,minimum_payout_amount,payout_holding_days")
        .eq("id", true)
        .maybeSingle();
      return data as { platform_commission_rate: number; minimum_payout_amount: number; payout_holding_days: number } | null;
    },
  });

  const recentClicks = useQuery({
    queryKey: ["admin-recent-clicks"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clicks")
        .select("id,created_at,source,creator:creators(handle,name),product:products(title,brand,image_key)")
        .order("created_at", { ascending: false })
        .limit(8);
      if (error || !data) return [] as any[];
      return data;
    },
  });

  const recentApplicants = useQuery({
    queryKey: ["admin-recent-applicants"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("creators")
        .select("id,handle,name,avatar_url,status,created_at,followers")
        .order("created_at", { ascending: false })
        .limit(6);
      if (error || !data) return [] as any[];
      return data;
    },
  });

  const topCreators = useQuery({
    queryKey: ["admin-top-creators-7d", sevenDaysAgo],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clicks")
        .select("creator_id,creator:creators(handle,name,avatar_url)")
        .gte("created_at", sevenDaysAgo)
        .not("creator_id", "is", null)
        .limit(1000);
      if (error || !data) return [] as any[];
      const counts = new Map<string, { count: number; creator: { handle: string; name: string; avatar_url: string | null } | null }>();
      for (const row of data) {
        const id = (row as { creator_id: string | null }).creator_id;
        if (!id) continue;
        const c = counts.get(id) ?? { count: 0, creator: ((row as unknown) as { creator: { handle: string; name: string; avatar_url: string | null } | null }).creator };
        c.count += 1;
        counts.set(id, c);
      }
      const result = Array.from(counts.entries())
        .sort((a, b) => b[1].count - a[1].count)
        .slice(0, 5)
        .map(([id, v]) => ({ id, count: v.count, creator: v.creator }));
      return result;
    },
  });

  const activitySeries = useQuery({
    queryKey: ["admin-activity-14d"],
    queryFn: async () => {
      const start = since(14);
      const [{ data: clicks }, { data: comms }] = await Promise.all([
        supabase.from("clicks").select("created_at").gte("created_at", start).limit(5000),
        supabase.from("commissions").select("created_at,commission_amount,status").gte("created_at", start).limit(5000),
      ]);
      const days: { day: string; label: string; clicks: number; revenue: number }[] = [];
      for (let i = 13; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        d.setHours(0, 0, 0, 0);
        days.push({
          day: d.toISOString().slice(0, 10),
          label: d.toLocaleDateString("en-GB", { day: "numeric", month: "short" }),
          clicks: 0,
          revenue: 0,
        });
      }
      const byDay = new Map(days.map((d) => [d.day, d]));
      for (const c of clicks ?? []) {
        const k = (c as { created_at: string }).created_at.slice(0, 10);
        const bucket = byDay.get(k);
        if (bucket) bucket.clicks += 1;
      }
      for (const c of comms ?? []) {
        const row = c as { created_at: string; commission_amount: number; status: string };
        if (row.status === "cancelled") continue;
        const k = row.created_at.slice(0, 10);
        const bucket = byDay.get(k);
        if (bucket) bucket.revenue += Number(row.commission_amount || 0);
      }
      return days;
    },
  });

  const m = metrics.data;
  const series = activitySeries.data ?? [];
  const totalRevenue = series.reduce((s, d) => s + d.revenue, 0);
  const conversionRate = m && m.clicks30d > 0 ? ((m.commissionsCount / m.clicks30d) * 100).toFixed(1) : null;

  return (
    <div className="space-y-10">
      <SeedTestProductsButton />
      <section className="flex flex-wrap items-center justify-between gap-3 border border-border bg-card p-4">
        <div className="flex items-center gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Platform commission rate</p>
            <p className="mt-1 font-serif text-2xl">
              {settings.data ? `${(Number(settings.data.platform_commission_rate) * 100).toFixed(2)}%` : "…"}
            </p>
          </div>
          {settings.data && (
            <div className="hidden text-xs text-muted-foreground sm:block">
              Min payout {fmtMoney(Number(settings.data.minimum_payout_amount))} · {settings.data.payout_holding_days}d hold
            </div>
          )}
        </div>
        <Link
          to="/admin/settings"
          className="inline-flex items-center gap-1 border border-border px-3 py-2 text-xs uppercase tracking-widest hover:bg-accent hover:text-accent-foreground"
        >
          <Pencil className="h-3 w-3" /> Edit
        </Link>
      </section>

      <section className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <StatCard
          icon={<Users className="h-4 w-4" />}
          label="Live creators"
          value={m?.liveCreators}
          hint={m ? `${m.pendingCreators} pending · ${m.totalCreators} total` : undefined}
        />
        <StatCard
          icon={<MousePointerClick className="h-4 w-4" />}
          label="Clicks (7d)"
          value={m?.clicks7d}
          hint={m ? `${m.clicks30d.toLocaleString()} in last 30d` : undefined}
        />
        <StatCard
          icon={<Coins className="h-4 w-4" />}
          label="Pending commission"
          value={m ? fmtMoney(m.pendingCommission) : undefined}
          hint={m ? `${fmtMoney(m.paidCommission)} paid · ${m.commissionsCount} orders` : undefined}
          raw
        />
        <StatCard
          icon={<TrendingUp className="h-4 w-4" />}
          label="Revenue (14d)"
          value={fmtMoney(totalRevenue)}
          hint={conversionRate ? `${conversionRate}% conv. rate (30d)` : "Awaiting first sale"}
          raw
        />
        <StatCard
          icon={<ShoppingBag className="h-4 w-4" />}
          label="Catalog products"
          value={m?.products}
          hint="From affiliate sync"
        />
        <StatCard
          icon={<Sparkles className="h-4 w-4" />}
          label="Approval queue"
          value={m?.pendingCreators}
          hint={m?.pendingCreators ? "Review pending" : "All clear"}
        />
      </section>

      <section className="border border-border bg-card">
        <Header title="Activity — last 14 days" link={{ to: "/admin/commissions", label: "Commissions" }} />
        <div className="p-4">
          {activitySeries.isLoading ? (
            <div className="h-56 animate-pulse bg-muted/40" />
          ) : (
            <div className="h-56 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={series} margin={{ top: 10, right: 12, bottom: 0, left: -12 }}>
                  <defs>
                    <linearGradient id="clicksGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--accent, 24 70% 50%))" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="hsl(var(--accent, 24 70% 50%))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} stroke="currentColor" strokeOpacity={0.3} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 10 }} stroke="currentColor" strokeOpacity={0.3} tickLine={false} axisLine={false} width={32} />
                  <Tooltip
                    contentStyle={{ background: "var(--background)", border: "1px solid var(--border)", borderRadius: 0, fontSize: 12 }}
                    labelStyle={{ color: "var(--foreground)" }}
                  />
                  <Area type="monotone" dataKey="clicks" stroke="var(--accent)" strokeWidth={2} fill="url(#clicksGrad)" name="Clicks" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </section>

      <section className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 border border-border bg-card">
          <Header title="Recent clicks" link={{ to: "/admin/affiliates", label: "Sync status" }} />
          <div className="divide-y divide-border">
            {recentClicks.isLoading && <Empty>Loading…</Empty>}
            {!recentClicks.isLoading && (recentClicks.data?.length ?? 0) === 0 && <Empty>No clicks yet.</Empty>}
            {recentClicks.data?.map((c) => {
              const product = c.product as unknown as { title: string; brand: string | null; image_key: string | null } | null;
              const creator = c.creator as unknown as { handle: string; name: string } | null;
              return (
                <div key={c.id} className="flex items-center gap-3 px-5 py-3">
                  <img
                    src={resolveImage(product?.image_key ?? null)}
                    alt={product?.title ?? "Product image"}
                    className="h-10 w-10 shrink-0 object-cover"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm">{product?.title ?? "Unknown product"}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {product?.brand ?? "—"} · {creator ? `@${creator.handle}` : "direct"}
                    </p>
                  </div>
                  <span className="shrink-0 text-xs text-muted-foreground">{fmtRelative(c.created_at)}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="border border-border bg-card">
          <Header title="Top creators (7d)" link={{ to: "/admin/creators", label: "All creators" }} />
          <div className="divide-y divide-border">
            {topCreators.isLoading && <Empty>Loading…</Empty>}
            {!topCreators.isLoading && (topCreators.data?.length ?? 0) === 0 && <Empty>No tracked clicks yet.</Empty>}
            {topCreators.data?.map((row, i) => (
              <div key={row.id} className="flex items-center gap-3 px-5 py-3">
                <span className="w-4 text-xs text-muted-foreground">{i + 1}</span>
                <img
                  src={resolveImage(row.creator?.avatar_url ?? null)}
                  alt={row.creator?.name ?? "Creator"}
                  className="h-8 w-8 shrink-0 rounded-full object-cover"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm">{row.creator?.name ?? "Unknown"}</p>
                  <p className="truncate text-xs text-muted-foreground">@{row.creator?.handle}</p>
                </div>
                <span className="shrink-0 text-xs">{row.count} clicks</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border border-border bg-card">
        <Header title="Recent applicants" link={{ to: "/admin/creators", label: "Review queue" }} />
        <div className="divide-y divide-border">
          {recentApplicants.isLoading && <Empty>Loading…</Empty>}
          {!recentApplicants.isLoading && (recentApplicants.data?.length ?? 0) === 0 && <Empty>No applications yet.</Empty>}
          {recentApplicants.data?.map((a) => (
            <div key={a.id} className="flex items-center gap-3 px-5 py-3">
              <img src={resolveImage(a.avatar_url)} alt={a.name} className="h-10 w-10 rounded-full object-cover" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm">{a.name} <span className="text-muted-foreground">@{a.handle}</span></p>
                <p className="truncate text-xs text-muted-foreground">
                  {a.followers ? `${a.followers} followers · ` : ""}
                  Applied {fmtRelative(a.created_at)}
                </p>
              </div>
              <StatusPill status={a.status as "pending" | "approved" | "live" | "rejected"} />
            </div>
          ))}
        </div>
      </section>

      <AnalyticsSection />
    </div>
  );
}

// ============================================================
// Analytics
// ============================================================

function SeedTestProductsButton() {
  const visible =
    import.meta.env.DEV ||
    (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("seed") === "true");
  const run = useServerFn(seedTestProducts);
  const mutation = useMutation({
    mutationFn: () => run(),
    onSuccess: (res) => {
      toast.success(`${res?.count ?? 10} test products added. Now click Reindex Algolia.`);
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to seed products");
    },
  });
  if (!visible) return null;
  return (
    <section className="flex items-center justify-between gap-3 border border-dashed border-border bg-muted/30 p-4">
      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground">Dev utility</p>
        <p className="mt-1 text-sm">Seed 10 test products to verify the search pipeline.</p>
      </div>
      <button
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
        className="inline-flex items-center gap-1 border border-border bg-background px-3 py-2 text-xs uppercase tracking-widest hover:bg-accent hover:text-accent-foreground disabled:opacity-50"
      >
        {mutation.isPending ? "Seeding…" : "Seed test products"}
      </button>
    </section>
  );
}

function AnalyticsSection() {
  const fetchAnalytics = useServerFn(getAdminAnalytics);
  const analytics = useQuery({
    queryKey: ["admin-analytics-12m"],
    queryFn: () => fetchAnalytics(),
    staleTime: 60_000,
  });

  if (analytics.isLoading) {
    return (
      <section className="space-y-4">
        <div className="h-7 w-48 animate-pulse bg-muted/50" />
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-64 animate-pulse bg-muted/40" />
          ))}
        </div>
      </section>
    );
  }

  if (analytics.isError || !analytics.data) {
    return (
      <section>
        <p className="text-sm text-muted-foreground">Couldn't load analytics.</p>
      </section>
    );
  }

  const a = analytics.data;

  return (
    <>
      <section className="space-y-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Analytics</p>
          <h2 className="mt-1 font-serif text-3xl">Last 12 months</h2>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <ChartCard title="Platform revenue">
            {a.monthlyRevenue.every((m) => m.amount === 0) ? (
              <ChartEmpty />
            ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={a.monthlyRevenue} margin={{ top: 8, right: 12, bottom: 0, left: -8 }}>
                <XAxis dataKey="month" tick={{ fontSize: 10 }} stroke="currentColor" strokeOpacity={0.3} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 10 }} stroke="currentColor" strokeOpacity={0.3} tickLine={false} axisLine={false} width={42} tickFormatter={(v) => `£${Number(v).toLocaleString("en-GB")}`} />
                <Tooltip cursor={{ fill: "color-mix(in oklab, var(--muted) 40%, transparent)" }} contentStyle={chartTooltip} formatter={(v: number) => [fmtMoney(Number(v)), "Revenue"]} />
                <Bar dataKey="amount" fill="var(--accent)" radius={0} />
              </BarChart>
            </ResponsiveContainer>
            )}
          </ChartCard>

          <ChartCard title="Creator growth">
            {a.creatorGrowth.every((m) => m.total === 0) ? (
              <ChartEmpty />
            ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={a.creatorGrowth} margin={{ top: 8, right: 12, bottom: 0, left: -16 }}>
                <XAxis dataKey="month" tick={{ fontSize: 10 }} stroke="currentColor" strokeOpacity={0.3} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 10 }} stroke="currentColor" strokeOpacity={0.3} tickLine={false} axisLine={false} width={32} />
                <Tooltip contentStyle={chartTooltip} formatter={(v: number) => [Number(v).toLocaleString(), "Creators"]} />
                <Line type="monotone" dataKey="total" stroke="var(--foreground)" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
            )}
          </ChartCard>

          <ChartCard title="Applications">
            {a.monthlyApplications.every((m) => m.count === 0) ? (
              <ChartEmpty />
            ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={a.monthlyApplications} margin={{ top: 8, right: 12, bottom: 0, left: -16 }}>
                <XAxis dataKey="month" tick={{ fontSize: 10 }} stroke="currentColor" strokeOpacity={0.3} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 10 }} stroke="currentColor" strokeOpacity={0.3} tickLine={false} axisLine={false} width={32} />
                <Tooltip cursor={{ fill: "color-mix(in oklab, var(--muted) 40%, transparent)" }} contentStyle={chartTooltip} formatter={(v: number) => [Number(v).toLocaleString(), "Applications"]} />
                <Bar dataKey="count" fill="var(--foreground)" radius={0} />
              </BarChart>
            </ResponsiveContainer>
            )}
          </ChartCard>

          <ChartCard title="Network performance (30d)">
            {a.networkPerformance.length === 0 || a.networkPerformance.every((n) => n.amount === 0 && n.orders === 0) ? (
              <ChartEmpty />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart layout="vertical" data={a.networkPerformance} margin={{ top: 8, right: 16, bottom: 0, left: 8 }}>
                  <XAxis type="number" tick={{ fontSize: 10 }} stroke="currentColor" strokeOpacity={0.3} tickLine={false} axisLine={false} tickFormatter={(v) => `£${Number(v).toLocaleString("en-GB")}`} />
                  <YAxis type="category" dataKey="network" tick={{ fontSize: 11 }} stroke="currentColor" strokeOpacity={0.3} tickLine={false} axisLine={false} width={70} />
                  <Tooltip cursor={{ fill: "color-mix(in oklab, var(--muted) 40%, transparent)" }} contentStyle={chartTooltip} formatter={(v: number, _n, p: { payload?: { orders?: number } }) => [`${fmtMoney(Number(v))} · ${p?.payload?.orders ?? 0} orders`, "Gross sales"]} />
                  <Bar dataKey="amount" fill="var(--accent)" radius={0} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="border border-border bg-card">
          <Header title="Top products this month" />
          {a.topProducts.length === 0 ? (
            <Empty>No commissions yet this month.</Empty>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-[10px] uppercase tracking-widest text-muted-foreground">
                  <th className="px-5 py-2">#</th>
                  <th className="px-2 py-2">Product</th>
                  <th className="px-2 py-2">Network</th>
                  <th className="px-5 py-2 text-right">Commissions</th>
                </tr>
              </thead>
              <tbody>
                {a.topProducts.map((p, i) => (
                  <tr key={p.id} className="border-b border-border last:border-0">
                    <td className="px-5 py-3 text-xs text-muted-foreground">{i + 1}</td>
                    <td className="px-2 py-3">
                      <p className="truncate">{p.title}</p>
                      <p className="truncate text-xs text-muted-foreground">{p.brand ?? "—"}</p>
                    </td>
                    <td className="px-2 py-3 text-xs text-muted-foreground">{p.network ?? "—"}</td>
                    <td className="px-5 py-3 text-right tabular-nums">{fmtMoney(p.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <AtRiskCreators creators={a.atRiskCreators} />
      </section>
    </>
  );
}

const chartTooltip = {
  background: "var(--background)",
  border: "1px solid var(--border)",
  borderRadius: 0,
  fontSize: 12,
} as const;

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border border-border bg-card">
      <div className="border-b border-border px-5 py-3">
        <h3 className="font-serif text-lg">{title}</h3>
      </div>
      <div className="p-4" style={{ height: 240 }}>
        {children}
      </div>
    </div>
  );
}

function ChartEmpty() {
  return (
    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
      No data yet
    </div>
  );
}

function AtRiskCreators({ creators }: { creators: { id: string; handle: string; name: string; avatar_url: string | null; userId: string }[] }) {
  const sendNudge = useServerFn(sendCreatorNudge);
  const [statusById, setStatusById] = useState<Record<string, "idle" | "sending" | "sent" | "error">>({});

  const nudge = useMutation({
    mutationFn: (creatorId: string) => sendNudge({ data: { creatorId } }),
    onMutate: (id) => setStatusById((s) => ({ ...s, [id]: "sending" })),
    onSuccess: (res, id) => setStatusById((s) => ({ ...s, [id]: res?.ok ? "sent" : "error" })),
    onError: (_e, id) => setStatusById((s) => ({ ...s, [id]: "error" })),
  });

  return (
    <div className="border border-border bg-card">
      <Header title="At-risk creators" />
      <p className="px-5 py-2 text-xs text-muted-foreground">Live creators with no clicks in 30 days.</p>
      {creators.length === 0 ? (
        <Empty>Everyone's been active in the last 30 days. 🎉</Empty>
      ) : (
        <div className="divide-y divide-border">
          {creators.map((c) => {
            const st = statusById[c.id] ?? "idle";
            return (
              <div key={c.id} className="flex items-center gap-3 px-5 py-3">
                <img src={resolveImage(c.avatar_url)} alt={c.name} className="h-9 w-9 rounded-full object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm">{c.name}</p>
                  <p className="truncate text-xs text-muted-foreground">@{c.handle}</p>
                </div>
                <button
                  type="button"
                  disabled={st === "sending" || st === "sent"}
                  onClick={() => nudge.mutate(c.id)}
                  className="inline-flex items-center gap-1 border border-border px-3 py-1.5 text-[10px] uppercase tracking-widest hover:bg-foreground hover:text-background disabled:opacity-50"
                >
                  <Mail className="h-3 w-3" />
                  {st === "sending" ? "Sending…" : st === "sent" ? "Sent" : st === "error" ? "Retry" : "Send nudge"}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  hint,
  raw,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | string | undefined;
  hint?: string;
  raw?: boolean;
}) {
  const display =
    value === undefined ? "…" : raw || typeof value === "string" ? value : value.toLocaleString();
  return (
    <div className="border border-border bg-card p-3 sm:p-5">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
        {icon}
        <span className="truncate">{label}</span>
      </div>
      <p className="mt-2 sm:mt-3 font-serif text-2xl sm:text-3xl break-words">{display}</p>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function Header({ title, link }: { title: string; link?: { to: "/admin/creators" | "/admin/affiliates" | "/admin/commissions"; label: string } }) {
  return (
    <div className="flex items-center justify-between border-b border-border px-5 py-3">
      <h3 className="font-serif text-lg">{title}</h3>
      {link && (
        <Link to={link.to} className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground">
          {link.label} <ArrowUpRight className="h-3 w-3" />
        </Link>
      )}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="px-5 py-6 text-sm text-muted-foreground">{children}</p>;
}

function StatusPill({ status }: { status: "pending" | "approved" | "live" | "rejected" }) {
  const tone: Record<string, string> = {
    pending: "bg-amber-100 text-amber-900",
    approved: "bg-emerald-100 text-emerald-900",
    live: "bg-emerald-700 text-white",
    rejected: "bg-rose-100 text-rose-900",
  };
  return <span className={`px-2 py-0.5 text-[10px] uppercase tracking-widest ${tone[status]}`}>{status}</span>;
}
